import {
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useEffect,
} from "react";
import {
  buildAssistantResponseMessage,
  type ThreadLiveState,
} from "./chat-session-helpers";
import type { ChatMessage } from "./types";
import type { ThreadLiveStates } from "./use-thread-live-states";

export const useLiveReplyPlayback = ({
  isChatView,
  bottomRef,
  wasAtBottomRef,
  messages,
  setMessages,
  isBusy,
  hasUnplayedLiveText,
  displayedLiveText,
  progressSteps,
  selectedThreadId,
  selectedThreadLiveState,
  liveStates: { updateThreadLiveState, clearLiveResponse },
}: {
  isChatView: boolean;
  bottomRef: RefObject<HTMLDivElement | null>;
  wasAtBottomRef: MutableRefObject<boolean>;
  messages: ChatMessage[];
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  isBusy: boolean;
  hasUnplayedLiveText: boolean;
  displayedLiveText: string;
  progressSteps: string[];
  selectedThreadId: string | null;
  selectedThreadLiveState: ThreadLiveState | undefined;
  liveStates: ThreadLiveStates;
}) => {
  useEffect(() => {
    if (!isChatView) return undefined;

    const viewport = bottomRef.current?.closest("[data-chat-scroll-viewport]");
    if (!(viewport instanceof HTMLElement)) return undefined;

    const handleScroll = () => {
      wasAtBottomRef.current =
        viewport.scrollTop + viewport.clientHeight >=
        viewport.scrollHeight - 80;
    };
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [isChatView, bottomRef, wasAtBottomRef]);

  useEffect(() => {
    if (!wasAtBottomRef.current) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [
    messages,
    isBusy,
    displayedLiveText,
    progressSteps,
    bottomRef,
    wasAtBottomRef,
  ]);

  useEffect(() => {
    if (!isBusy && !hasUnplayedLiveText) return undefined;

    const interval = setInterval(() => {
      if (!selectedThreadId) return;

      updateThreadLiveState(selectedThreadId, (state) => {
        if (state.displayedText.length >= state.targetText.length) return state;

        const remaining = state.targetText.length - state.displayedText.length;
        const step = Math.max(2, Math.ceil(remaining / 40));
        return {
          ...state,
          displayedText: state.targetText.slice(
            0,
            state.displayedText.length + step,
          ),
        };
      });
    }, 24);

    return () => clearInterval(interval);
  }, [isBusy, hasUnplayedLiveText, selectedThreadId, updateThreadLiveState]);

  useEffect(() => {
    if (!selectedThreadId || !selectedThreadLiveState?.pendingResponse) return;
    const pendingResponse = selectedThreadLiveState.pendingResponse;

    if (
      selectedThreadLiveState.displayedText.length <
      selectedThreadLiveState.targetText.length
    )
      return;

    setMessages((currentMessages) => [
      ...currentMessages,
      buildAssistantResponseMessage(
        pendingResponse,
        selectedThreadLiveState.progressSteps,
      ),
    ]);
    clearLiveResponse(selectedThreadId);
  }, [
    selectedThreadId,
    selectedThreadLiveState,
    clearLiveResponse,
    setMessages,
  ]);
};
